import { type ReactNode, useEffect } from "react";
import { LogOut } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuItem, useSidebar } from "@/shared/components/ui/sidebar";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useContactPhotoUrl } from "@/shared/hooks/useContactPhotoUrl";
import { getInitials, getDisplayName } from "@/shared/utils/formatters";

function UserAvatar() {
  const { user, authUser } = useAppContext();
  const { contacts } = useUserContacts();
  const email = authUser?.email;

  const meContact = contacts?.find((c) => c.email === email);
  const sessionKey = email ? `nav-avatar:${email}` : null;
  const cachedUrl = sessionKey ? sessionStorage.getItem(sessionKey) : null;
  const { photoUrl } = useContactPhotoUrl(meContact?.id || null, cachedUrl);

  useEffect(() => {
    if (!sessionKey) return;
    if (photoUrl) {
      sessionStorage.setItem(sessionKey, photoUrl);
    } else if (photoUrl === null) {
      sessionStorage.removeItem(sessionKey);
    }
  }, [sessionKey, photoUrl]);

  const firstName = user?.first_name || "";
  const lastName = user?.last_name || "";
  const fullName = user?.full_name || "";
  const initials = fullName ? getInitials({ fullName }) : getInitials({ firstName, lastName });

  return (
    <div className="flex size-8 items-center justify-center rounded-full bg-brand text-white font-semibold text-2xs overflow-hidden flex-shrink-0">
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={`${firstName} ${lastName}`}
          className="w-full h-full object-cover rounded-full"
        />
      ) : (
        initials || "U"
      )}
    </div>
  );
}

interface SidebarUserFooterProps {
  trigger: ReactNode;
  menuItems?: ReactNode;
  onLogout: () => void;
}

export function SidebarUserFooter({ trigger, menuItems, onLogout }: SidebarUserFooterProps) {
  const { isMobile } = useSidebar();
  const { user, authUser } = useAppContext();

  const displayName = getDisplayName({
    fullName: user?.full_name,
    firstName: user?.first_name,
    lastName: user?.last_name,
    email: authUser?.email,
  });

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <div className="flex items-center gap-2 p-2">
          <UserAvatar />
          <p className="flex-1 truncate text-sm font-medium">{displayName}</p>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              {menuItems}
              {menuItems && <DropdownMenuSeparator />}
              <DropdownMenuItem onClick={onLogout}>
                <LogOut className="mr-2 h-4 w-4 text-destructive" />
                <span className="text-destructive">Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
